import { useState } from 'react'
import { format, parseISO } from 'date-fns'
import { useTasks } from '../hooks/useTasks'
import { Button } from '../../../components/ui/Button'
import { Spinner } from '../../../components/ui/Spinner'
import { cn } from '../../../lib/utils'
import { Portal } from '../../../components/Portal'

export const TaskDetail = ({ task, member, onEdit, onClose }) => {
  const { deleteTask, completeTask, uncompleteTask } = useTasks()
  const [deleting,       setDeleting]       = useState(false)
  const [toggling,       setToggling]       = useState(false)
  const [confirmDelete,  setConfirmDelete]  = useState(false)

  const done = !!task.completed_at

  const handleToggle = async () => {
    setToggling(true)
    try {
      done ? await uncompleteTask(task.id) : await completeTask(task)
      onClose()
    } finally { setToggling(false) }
  }

  const handleDelete = async () => {
    setDeleting(true)
    try { await deleteTask(task.id); onClose() }
    finally { setDeleting(false) }
  }

  return (
    <Portal>
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center pb-[env(safe-area-inset-bottom,0px)] sm:pb-0 overflow-hidden" onClick={onClose}>
      <div className="absolute inset-0 bg-black/40" />
      <div
        className="relative bg-surface rounded-t-2xl sm:rounded-2xl w-full max-w-lg max-h-[80dvh] flex flex-col shadow-card"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between px-5 pt-5 pb-3 border-b border-border">
          <h2 className="text-lg font-bold text-text-primary pr-4 leading-tight">{task.title}</h2>
          <button onClick={onClose} className="text-text-muted hover:text-text-primary p-1 flex-shrink-0">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M18 6 6 18M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="overflow-y-auto flex-1 min-h-0 px-5 py-4 space-y-3">
          {task.description && (
            <p className="text-sm text-text-secondary whitespace-pre-wrap">{task.description}</p>
          )}

          {task.due_date && (
            <div className="flex items-center gap-2 text-sm text-text-secondary">
              <svg className="w-4 h-4 text-text-muted" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <rect x="3" y="4" width="18" height="18" rx="2" />
                <path d="M16 2v4M8 2v4M3 10h18" />
              </svg>
              Due {format(parseISO(task.due_date), 'EEEE, MMMM d, yyyy')}
            </div>
          )}

          {member?.profiles && (
            <div className="flex items-center gap-2 text-sm text-text-secondary">
              <span
                className="w-6 h-6 rounded-full text-[10px] font-bold text-white flex items-center justify-center flex-shrink-0"
                style={{ backgroundColor: member.profiles.member_color ?? '#7c6faf' }}
              >
                {member.profiles.display_name?.[0]}
              </span>
              Assigned to {member.profiles.display_name}
            </div>
          )}

          {task.recurrence_rule && (
            <div className="text-sm text-text-muted flex items-center gap-2">
              <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M3 12a9 9 0 1 0 9-9 9.75 9.75 0 0 0-6.74 2.74L3 8" />
                <path d="M3 3v5h5" />
              </svg>
              Recurring task
            </div>
          )}

          {done && task.completed_at && (
            <div className={cn('text-sm font-medium text-emerald-600')}>
              Completed {format(parseISO(task.completed_at), 'MMM d, yyyy')}
            </div>
          )}
        </div>

        <div className="px-5 py-4 border-t border-border flex gap-2">
          {confirmDelete ? (
            <>
              <Button variant="secondary" size="sm" onClick={() => setConfirmDelete(false)} className="flex-1">Cancel</Button>
              <Button variant="danger" size="sm" onClick={handleDelete} disabled={deleting} className="flex-1">
                {deleting ? <Spinner size="sm" /> : 'Delete'}
              </Button>
            </>
          ) : (
            <>
              <Button
                variant="danger"
                size="sm"
                onClick={() => setConfirmDelete(true)}
                className="flex-shrink-0"
              >
                Delete
              </Button>
              <Button variant="secondary" size="sm" onClick={() => onEdit(task)} className="flex-1">
                Edit
              </Button>
              <Button size="sm" onClick={handleToggle} disabled={toggling} className="flex-1">
                {toggling ? <Spinner size="sm" /> : done ? 'Reopen' : 'Complete'}
              </Button>
            </>
          )}
        </div>
      </div>
    </div>
    </Portal>
  )
}
